import { useEffect, useMemo, useState } from 'react'
import { SlideOver } from './SlideOver.js'
import {
  fetchAdhocPoPreview, raiseAdhocPo, searchPoSuppliers,
  type AdhocPoView, type AdhocPreview, type XeroContact,
} from '../lib/api.js'

/**
 * Decision 236 — "Purchase order…" on an accepted quote: an ad hoc DRAFT PO
 * in Xero from the lines the customer accepted.
 *
 *   1. The purchase orders already on this quote (ad hoc and the distributor's),
 *      with their live status; a DRAFT ad hoc PO can be reopened to edit.
 *   2. The supplier — a live Xero contact search, picked every time.
 *   3. The accepted lines — tick what this order buys. Qty = the accepted
 *      quantity and cost = the quote's buy (one year for a recurring line),
 *      both editable; a changed figure is marked. A line already on a live
 *      PO says so and starts unticked.
 *
 * Nothing is written until the button is pressed; the server re-checks all of it.
 */

const gbp = (n: number) => `£${n.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
const r2 = (n: number) => Math.round(n * 100) / 100
const num = (s: string) => (s.trim() === '' ? Number.NaN : Number(s))

interface Row { ticked: boolean; qty: string; cost: string }

export function AdhocPurchaseOrder({ quoteId, quoteNumber, onClose, onRaised }: {
  quoteId: string
  quoteNumber: string
  onClose: () => void
  onRaised: () => void | Promise<void>
}) {
  const [preview, setPreview] = useState<AdhocPreview | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [editing, setEditing] = useState<AdhocPoView | null>(null)
  const [rows, setRows] = useState<Record<string, Row>>({})
  const [supplier, setSupplier] = useState<{ contactId: string; name: string } | null>(null)
  const [term, setTerm] = useState('')
  const [hits, setHits] = useState<XeroContact[] | null>(null)
  const [done, setDone] = useState<{ po: AdhocPoView; action: 'created' | 'updated'; warnings: string[] } | null>(null)

  const seed = (p: AdhocPreview, po: AdhocPoView | null) => {
    const next: Record<string, Row> = {}
    for (const l of p.lines) {
      const stored = po?.lines.find((s) => s.quoteItemId === l.quoteItemId)
      const elsewhere = l.onOrder.filter((o) => o.poId !== po?.id)
      next[l.quoteItemId] = stored
        ? { ticked: true, qty: String(stored.qty), cost: String(stored.unitCost) }
        : { ticked: !po && elsewhere.length === 0 && l.accountCode !== null, qty: String(l.acceptedQty), cost: String(l.quoteUnitCost) }
    }
    setRows(next)
    setSupplier(po && po.contactId ? { contactId: po.contactId, name: po.contactName } : null)
    setHits(null); setTerm('')
  }

  const load = async (keepDone = false) => {
    setError(null)
    try {
      const p = await fetchAdhocPoPreview(quoteId)
      setPreview(p); setEditing(null); seed(p, null)
      if (!keepDone) setDone(null)
    } catch (e) { setError((e as Error).message) }
  }
  useEffect(() => { void load() }, [quoteId])   // eslint-disable-line react-hooks/exhaustive-deps

  const search = async () => {
    if (term.trim().length < 2) { setError('Type at least two characters of the supplier\'s name.'); return }
    setBusy(true); setError(null)
    try { setHits((await searchPoSuppliers(quoteId, term)).contacts) } catch (e) { setError((e as Error).message) } finally { setBusy(false) }
  }

  const picked = useMemo(() => {
    if (!preview) return []
    return preview.lines.filter((l) => rows[l.quoteItemId]?.ticked).map((l) => {
      const r = rows[l.quoteItemId]
      const qty = num(r.qty); const cost = num(r.cost)
      const valid = Number.isFinite(qty) && qty > 0 && Number.isFinite(cost) && cost >= 0
      return { line: l, qty, cost, valid, total: valid ? r2(qty * cost) : 0 }
    })
  }, [preview, rows])
  const subTotal = r2(picked.reduce((s, p) => s + p.total, 0))
  const invalid = picked.filter((p) => !p.valid).length
  const unmapped = picked.filter((p) => p.line.accountCode === null).length

  const submit = async () => {
    if (!supplier) return
    setBusy(true); setError(null)
    try {
      const r = await raiseAdhocPo(quoteId, {
        contactId: supplier.contactId, contactName: supplier.name, poId: editing?.id ?? null,
        lines: picked.map((p) => ({ quoteItemId: p.line.quoteItemId, qty: r2(p.qty), unitCost: r2(p.cost) })),
      })
      setDone({ po: r.po, action: r.action, warnings: r.warnings })
      await onRaised()
      await load(true)
    } catch (e) { setError((e as Error).message) } finally { setBusy(false) }
  }

  const setRow = (id: string, patch: Partial<Row>) => setRows((cur) => ({ ...cur, [id]: { ...cur[id], ...patch } }))
  const blocked = !preview || preview.blockers.length > 0
  const allTicked = !!preview && preview.lines.length > 0 && preview.lines.every((l) => rows[l.quoteItemId]?.ticked)

  return (
    <SlideOver title={`Purchase order — ${quoteNumber}`} onClose={onClose} wide
      subtitle={preview ? `${preview.customerName ?? 'No customer'} · ${preview.quoteTitle} · from the customer's accepted lines` : undefined}>
      {error && <div className="banner banner--stop" data-testid="adhoc-po-error"><span style={{ whiteSpace: 'pre-line' }}>{error}</span><button className="btn btn--ghost btn--sm" onClick={() => setError(null)}>Dismiss</button></div>}
      {!preview && !error && <p className="muted">Reading the accepted record and checking Xero…</p>}
      {done && (
        <div className="banner" data-testid="adhoc-po-done">
          <span>
            {done.action === 'created' ? 'Draft purchase order raised' : 'Draft purchase order updated'}
            {done.po.xeroPoNumber ? <> — <strong>{done.po.xeroPoNumber}</strong></> : null} to {done.po.contactName}.{' '}
            <a href={done.po.url} target="_blank" rel="noreferrer">Open in Xero →</a> — review, then approve and send it from there.
            {done.warnings.map((w) => <span key={w} className="muted small" style={{ display: 'block' }}>⚠ {w}</span>)}
          </span>
        </div>
      )}
      {preview && (
        <>
          {preview.blockers.map((b) => <div key={b} className="banner banner--stop" data-testid="adhoc-po-blocker"><span>{b}</span></div>)}
          {preview.warnings.map((w) => <div key={w} className="banner" data-testid="adhoc-po-warning"><span>⚠ {w}</span></div>)}

          {preview.purchaseOrders.length > 0 && (
            <>
              <h3 style={{ margin: '4px 0 8px' }}>Purchase orders on this quote</h3>
              <div className="table-wrap" style={{ marginBottom: 16 }}>
                <table data-testid="adhoc-po-existing">
                  <thead><tr><th>PO</th><th>Supplier</th><th>Type</th><th style={{ textAlign: 'right' }}>Lines</th><th style={{ textAlign: 'right' }}>Sub-total</th><th>Status</th><th>Raised</th><th></th></tr></thead>
                  <tbody>
                    {preview.purchaseOrders.map((po) => (
                      <tr key={po.id} style={editing?.id === po.id ? { background: 'var(--surface-2, rgba(0,153,204,.08))' } : undefined}>
                        <td><a href={po.url} target="_blank" rel="noreferrer"><strong>{po.xeroPoNumber ?? 'Draft'}</strong></a></td>
                        <td>{po.contactName}</td>
                        <td className="small">{po.kind === 'adhoc' ? 'Ad hoc' : 'Distributor quote'}</td>
                        <td style={{ textAlign: 'right' }}>{po.lineCount}</td>
                        <td style={{ textAlign: 'right' }}>{po.subTotal == null ? '—' : gbp(po.subTotal)}</td>
                        <td><span className={`pill ${po.xeroStatus === 'DRAFT' ? 'pill--info' : po.xeroStatus === 'DELETED' ? 'pill--stop' : 'pill--ok'}`}>{po.xeroStatus}</span></td>
                        <td className="small">{new Date(po.raisedAt).toLocaleDateString('en-GB')} · {po.raisedByName}</td>
                        <td>
                          {po.kind === 'adhoc' && po.canUpdate && !blocked && (
                            editing?.id === po.id
                              ? <button className="btn btn--sm btn--ghost" onClick={() => { setEditing(null); seed(preview, null) }}>Stop editing</button>
                              : <button className="btn btn--sm" data-testid="adhoc-po-edit" onClick={() => { setDone(null); setEditing(po); seed(preview, po) }}>Edit draft</button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}

          <h3 style={{ margin: '4px 0 8px' }}>{editing ? `Editing ${editing.xeroPoNumber ?? 'the draft'}` : 'New purchase order'}</h3>

          <dl className="kv" style={{ marginBottom: 10 }}>
            <dt>Supplier</dt>
            <dd>
              {supplier
                ? <><strong data-testid="adhoc-po-supplier">{supplier.name}</strong> <button className="btn btn--sm btn--ghost" disabled={blocked} onClick={() => setSupplier(null)}>Change</button></>
                : (
                  <span style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                    <input className="input" style={{ minWidth: 240 }} placeholder="Search Xero contacts…" value={term} disabled={blocked}
                      onChange={(e) => setTerm(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') void search() }} data-testid="adhoc-po-search" />
                    <button className="btn btn--sm" disabled={blocked || busy} onClick={() => void search()}>Search</button>
                  </span>
                )}
            </dd>
            <dt>Reference</dt><dd>{preview.quoteNumber} · {supplier?.name ?? <span className="muted">supplier</span>}</dd>
            <dt>Terms</dt><dd className="small">DRAFT · GBP · amounts ex VAT · tax {preview.settings.taxType || '—'} · purchase nominal per line (default {preview.settings.accountCode || '—'}) · delivery address = Xero's default</dd>
          </dl>
          {!supplier && hits && (
            <div className="table-wrap" style={{ marginBottom: 12 }}>
              <table data-testid="adhoc-po-hits">
                <thead><tr><th>Xero contact</th><th>Email</th><th>Supplier?</th><th></th></tr></thead>
                <tbody>
                  {hits.length === 0 && <tr><td colSpan={4} className="muted">No contacts match.</td></tr>}
                  {hits.map((c) => (
                    <tr key={c.contactId}>
                      <td>{c.name}</td>
                      <td className="small">{c.email ?? '—'}</td>
                      <td>{c.isSupplier ? 'yes' : <span className="muted">no</span>}</td>
                      <td><button className="btn btn--sm btn--primary" onClick={() => { setSupplier({ contactId: c.contactId, name: c.name }); setHits(null) }}>Use</button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="table-wrap">
            <table data-testid="adhoc-po-lines">
              <thead>
                <tr>
                  <th><input type="checkbox" aria-label="Tick every line" checked={allTicked} disabled={blocked}
                    onChange={(e) => setRows((cur) => Object.fromEntries(Object.entries(cur).map(([k, v]) => [k, { ...v, ticked: e.target.checked }])))} /></th>
                  <th>SKU</th><th>Line</th><th>Nominal</th><th>Already ordered</th>
                  <th style={{ textAlign: 'right' }}>Accepted</th><th style={{ textAlign: 'right' }}>Qty</th><th style={{ textAlign: 'right' }}>Unit cost</th><th style={{ textAlign: 'right' }}>Line (ex VAT)</th>
                </tr>
              </thead>
              <tbody>
                {preview.lines.length === 0 && <tr><td colSpan={9} className="muted">No accepted lines to order.</td></tr>}
                {preview.lines.map((l) => {
                  const r = rows[l.quoteItemId]
                  if (!r) return null
                  const qty = num(r.qty); const cost = num(r.cost)
                  const edited = r.ticked && (r2(qty) !== r2(l.acceptedQty) || r2(cost) !== r2(l.quoteUnitCost))
                  const elsewhere = l.onOrder.filter((o) => o.poId !== editing?.id)
                  return (
                    <tr key={l.quoteItemId} className={r.ticked ? undefined : 'muted'} data-testid="adhoc-po-line">
                      <td><input type="checkbox" checked={r.ticked} disabled={blocked} aria-label={`Order ${l.name}`}
                        onChange={(e) => setRow(l.quoteItemId, { ticked: e.target.checked })} /></td>
                      <td>{l.sku ? <code>{l.sku}</code> : <span className="muted small">{l.isAdhoc ? 'ad-hoc line' : '—'}</span>}</td>
                      <td>
                        {l.description}
                        {l.costBasis === 'year' && <> <span className="pill pill--info" title="A recurring line starts at ONE year's buy. Change the unit cost if the supplier bills the whole term upfront.">1 yr cost</span></>}
                      </td>
                      <td style={{ whiteSpace: 'nowrap' }}>
                        {l.accountCode
                          ? <>{l.accountCode}{l.salesNominal ? <span className="muted small"> ← {l.salesNominal}</span> : <span className="muted small"> (default)</span>}</>
                          : <span className="pill pill--stop" title={`Sales nominal ${l.salesNominal} has no purchase nominal — map it in Admin → Xero`}>{l.salesNominal} unmapped</span>}
                      </td>
                      <td className="small" data-testid="adhoc-po-onorder">
                        {elsewhere.length === 0 ? <span className="muted">—</span>
                          : elsewhere.map((o) => <span key={o.poId} className="pill pill--warn" style={{ marginRight: 4 }}>{o.poNumber ?? 'draft'} ×{o.qty}{o.status !== 'DRAFT' ? ` · ${o.status}` : ''}</span>)}
                      </td>
                      <td style={{ textAlign: 'right' }}>{l.acceptedQty}</td>
                      <td style={{ textAlign: 'right' }}>
                        <input className="input" style={{ width: 72, textAlign: 'right' }} inputMode="decimal" value={r.qty} disabled={!r.ticked || blocked}
                          onChange={(e) => setRow(l.quoteItemId, { qty: e.target.value })} aria-label={`Quantity for ${l.name}`} />
                      </td>
                      <td style={{ textAlign: 'right' }}>
                        <input className="input" style={{ width: 96, textAlign: 'right' }} inputMode="decimal" value={r.cost} disabled={!r.ticked || blocked}
                          onChange={(e) => setRow(l.quoteItemId, { cost: e.target.value })} aria-label={`Unit cost for ${l.name}`} />
                        {edited && <div className="small" style={{ color: 'var(--warn, #b45309)' }} title={`Quote: ${l.acceptedQty} × ${gbp(l.quoteUnitCost)}`}>edited</div>}
                      </td>
                      <td style={{ textAlign: 'right' }}>{r.ticked && Number.isFinite(qty) && Number.isFinite(cost) ? gbp(r2(qty * cost)) : '—'}</td>
                    </tr>
                  )
                })}
              </tbody>
              <tfoot><tr><td colSpan={8}><strong>{picked.length} line{picked.length === 1 ? '' : 's'} ticked · sub-total ex VAT</strong></td><td style={{ textAlign: 'right' }}><strong data-testid="adhoc-po-subtotal">{gbp(subTotal)}</strong></td></tr></tfoot>
            </table>
          </div>
          <p className="muted small" style={{ marginTop: 8 }}>
            Quantities start at what the customer accepted and costs at the quote's buy price (one year for a recurring line); change either for this order.
            A line already on another purchase order starts unticked. Xero assigns the PO number.
          </p>
          {invalid > 0 && <p className="pill pill--stop">{invalid} ticked line{invalid === 1 ? ' needs' : 's need'} a quantity above 0 and a cost of £0 or more.</p>}
          {unmapped > 0 && <p className="pill pill--stop">{unmapped} ticked line{unmapped === 1 ? ' has' : 's have'} an unmapped sales nominal — map it in Admin → Xero first.</p>}

          <div className="toolbar" style={{ marginTop: 14 }}>
            <button className="btn btn--primary" data-testid="adhoc-po-raise"
              disabled={blocked || busy || !supplier || picked.length === 0 || invalid > 0 || unmapped > 0}
              title={!supplier ? 'Choose the supplier first' : picked.length === 0 ? 'Tick at least one line' : undefined}
              onClick={() => void submit()}>
              {busy ? 'Sending to Xero…' : editing ? `Update ${editing.xeroPoNumber ?? 'the draft'}` : 'Raise draft PO'}
            </button>
            <button className="btn btn--ghost" onClick={onClose}>Close</button>
          </div>
        </>
      )}
    </SlideOver>
  )
}
